import type { TFunction } from 'i18next';

import { RealEstateAssetHistoryEntry } from '../real-estate-asset-management.dto';

/**
 * Maps an audit row's stable `field_id` to the i18n key whose translation is
 * shown to the user as the field's label. Reuses keys that already exist in
 * the translation files for the form (Name / Main address / etc.) so we don't
 * duplicate German wording for the same concept.
 */
const FIELD_LABEL_KEYS: Record<string, string> = {
    name: 'Name',
    mainAddress: 'Main address',
    buildingFunctionCode: 'REA function',
    notes: 'General notes',
};

/**
 * Decode a JSON-encoded audit value as the backend persists it in
 * `old_value` / `new_value`. The backend always JSON-encodes, even plain
 * strings (`"Hauptstraße 1"` is stored as `"\"Hauptstraße 1\""`). On decode
 * failure (legacy rows, unexpected shapes), fall back to the raw string so
 * the user still sees *something* rather than a blank entry.
 */
function decodeAuditValue(raw: string | null | undefined): unknown {
    if (raw == null) return null;
    try {
        return JSON.parse(raw);
    } catch {
        return raw;
    }
}

/**
 * Display-format a decoded audit value. Strings render as-is, primitives via
 * String(), objects/arrays as compact JSON. `null` collapses to a localized
 * "(empty)" placeholder so old/new lines never render a literal "null".
 */
export function formatAuditValue(raw: string | null | undefined, t: TFunction): string {
    const decoded = decodeAuditValue(raw);
    if (decoded == null) return t('(empty)');
    if (typeof decoded === 'string') return decoded;
    if (typeof decoded === 'number' || typeof decoded === 'boolean') return String(decoded);
    try {
        return JSON.stringify(decoded);
    } catch {
        return String(decoded);
    }
}

export interface FormattedHistoryEntry {
    occurredAt: string;
    actorName: string;
    headline: string;
    oldValue: string | null;
    newValue: string | null;
    /**
     * Some events (CREATE of an REA, relation add/remove) don't have an
     * old/new pair — the headline is the whole story. Toggled off so the
     * renderer can skip the two-line block for those rows.
     */
    showValues: boolean;
}

/**
 * Best-effort human label for a field. Looks up the stable `field_id` in
 * [FIELD_LABEL_KEYS] and runs the result through i18n (so German users see
 * "Hauptadresse" for "mainAddress"). Unknown field ids fall back to a
 * humanized version of the id ("mainAddress" → "Main Address") so newly-added
 * tracked fields don't break the History tab while translations catch up.
 */
function fieldLabel(fieldId: string | null | undefined, t: TFunction): string {
    if (!fieldId) return '';
    const knownKey = FIELD_LABEL_KEYS[fieldId];
    if (knownKey) return t(knownKey);
    return humanize(fieldId);
}

/**
 * Label for a COLLECTED_ITEM entry, where `field_id` is the requirement-field
 * `code`. The configured label comes from the requirement catalog (passed in
 * by the caller); a code with no catalog entry (e.g. a since-deleted field)
 * falls back to a humanized code ("energy_consumption" → "Energy consumption").
 */
function requirementFieldLabel(
    fieldId: string | null | undefined,
    requirementFieldLabels: Record<string, string> | undefined,
): string {
    if (!fieldId) return '';
    return requirementFieldLabels?.[fieldId] ?? humanize(fieldId);
}

function humanize(fieldId: string): string {
    return fieldId
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/^./, (c) => c.toUpperCase());
}

/**
 * Build the final, localized headline + values for a single history entry.
 * Branches on (entityType, changeType, fieldId) per the History contract.
 *
 * Keep this exhaustive — new entity types or change kinds should add a branch
 * here, not silently default to "value changed".
 */
export function formatHistoryEntry(
    entry: RealEstateAssetHistoryEntry,
    t: TFunction,
    locale?: string,
    requirementFieldLabels?: Record<string, string>,
): FormattedHistoryEntry {
    // `locale` is the active i18n language ("en" / "de") passed in by the
    // caller. Falling through `undefined` is also fine — `toLocaleString` then
    // uses the browser default — but we prefer to thread the app's language
    // explicitly so the date format matches the rest of the History tab.
    const occurredAt = new Date(entry.occurredAt).toLocaleString(locale);
    const actorName = entry.actor.name;

    if (entry.entityType === 'REAL_ESTATE_ASSET' && entry.changeType === 'CREATE') {
        return {
            occurredAt,
            actorName,
            headline: t('{{actor}} created this real estate asset.', { actor: actorName }),
            oldValue: null,
            newValue: null,
            showValues: false,
        };
    }

    if (entry.entityType === 'BUILDING' && entry.fieldId === 'realEstateAssetId') {
        // Use the existing decoder so we treat both DB NULL and the literal
        // JSON-encoded "null" string the same way — an unlink event in either
        // form has no current REA on the building.
        const linked = decodeAuditValue(entry.newValue) != null;
        return {
            occurredAt,
            actorName,
            headline: linked
                ? t('{{actor}} added a building to this asset.', { actor: actorName })
                : t('{{actor}} removed a building from this asset.', { actor: actorName }),
            oldValue: null,
            newValue: null,
            showValues: false,
        };
    }

    if (entry.entityType === 'COLLECTED_ITEM') {
        // Data-collection (enrichment) answers. `fieldId` carries the
        // requirement-field code; document uploads are distinguished by the
        // backend via a `.document`-suffixed data path.
        const field = requirementFieldLabel(entry.fieldId, requirementFieldLabels);
        const isDocumentUpload = entry.fieldDataPath?.endsWith('.document') ?? false;
        return {
            occurredAt,
            actorName,
            headline: isDocumentUpload
                ? t('{{actor}} uploaded a document for {{field}}.', { actor: actorName, field })
                : t('{{actor}} changed the value of {{field}}.', { actor: actorName, field }),
            oldValue: formatAuditValue(entry.oldValue, t),
            newValue: formatAuditValue(entry.newValue, t),
            showValues: true,
        };
    }

    if (entry.entityType === 'SERVICE_ASSET_MAPPING') {
        const isCreate = entry.changeType === 'CREATE';
        return {
            occurredAt,
            actorName,
            headline: isCreate
                ? t('{{actor}} linked a service to this asset.', { actor: actorName })
                : t('{{actor}} unlinked a service from this asset.', { actor: actorName }),
            oldValue: null,
            newValue: null,
            showValues: false,
        };
    }

    // Default: field update on the REA itself (name, mainAddress, etc.) or any
    // other entity type we may add in the future (METER, …).
    return {
        occurredAt,
        actorName,
        headline: t('{{actor}} changed the value of {{field}}.', {
            actor: actorName,
            field: fieldLabel(entry.fieldId, t),
        }),
        oldValue: formatAuditValue(entry.oldValue, t),
        newValue: formatAuditValue(entry.newValue, t),
        showValues: true,
    };
}
